import { createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";
import { generateConfig } from "../../utils/api";
import { handleApiError } from "../../utils/apiErrorHandler";

// Track a single click event (batched internally)
export const trackClick = createAsyncThunk(
  "analytics/trackClick",
  async (clickData, { rejectWithValue, getState, dispatch }) => {
    try {
      const state = getState();
      const token = state.auth.token;

      // Don't track if not authenticated
      if (!token) {
        return { sent: false, reason: "Not authenticated" };
      }

      // Add to batch in state (handled by reducer)
      return { clickData, sent: false };
    } catch (error) {
      return handleApiError(error, rejectWithValue, dispatch);
    }
  }
);

// Send batched clicks to server
export const sendClickBatch = createAsyncThunk(
  "analytics/sendClickBatch",
  async (_, { rejectWithValue, getState, dispatch }) => {
    try {
      const state = getState();
      const token = state.auth.token;
      const batch = state.analytics.clickBatch || [];

      if (batch.length === 0) {
        return { sent: false, reason: "Empty batch" };
      }

      const { baseURL, config } = generateConfig({ token });

      console.log(`[Analytics Thunk] Sending batch of ${batch.length} clicks`);

      const response = await axios.post(
        `${baseURL}/api/analytics/clicks/batch`,
        { clicks: batch },
        config
      );

      return { sent: true, batchSize: batch.length, data: response.data };
    } catch (error) {
      return handleApiError(error, rejectWithValue, dispatch);
    }
  }
);

// Get aggregated click analytics
export const getClickAnalytics = createAsyncThunk(
  "analytics/getClickAnalytics",
  async (filters = {}, { rejectWithValue, getState, dispatch }) => {
    try {
      const state = getState();
      const token = state.auth.token;
      const { baseURL, config } = generateConfig({ token });

      console.log(`[Analytics Thunk] Fetching analytics with filters:`, filters);

      const response = await axios.get(
        `${baseURL}/api/analytics/clicks/analysis`,
        { ...config, params: filters }
      );

      return response.data;
    } catch (error) {
      return handleApiError(error, rejectWithValue, dispatch);
    }
  }
);

// Get inefficiency analysis
export const getInefficiencies = createAsyncThunk(
  "analytics/getInefficiencies",
  async (daysBack = 30, { rejectWithValue, getState, dispatch }) => {
    try {
      const state = getState();
      const token = state.auth.token;
      const { baseURL, config } = generateConfig({ token });

      console.log(`[Analytics Thunk] Fetching inefficiencies for last ${daysBack} days`);

      const response = await axios.get(
        `${baseURL}/api/analytics/clicks/inefficiencies`,
        { ...config, params: { daysBack } }
      );

      return response.data;
    } catch (error) {
      return handleApiError(error, rejectWithValue, dispatch);
    }
  }
);

// Get page-specific summary
export const getPageSummary = createAsyncThunk(
  "analytics/getPageSummary",
  async ({ path, startDate, endDate }, { rejectWithValue, getState, dispatch }) => {
    try {
      const state = getState();
      const token = state.auth.token;
      const { baseURL, config } = generateConfig({ token });

      console.log(`[Analytics Thunk] Fetching page summary for ${path}`);

      const response = await axios.get(
        `${baseURL}/api/analytics/clicks/page-summary`,
        { ...config, params: { path, startDate, endDate } }
      );

      return response.data;
    } catch (error) {
      return handleApiError(error, rejectWithValue, dispatch);
    }
  }
);

// Get user journey
export const getUserJourney = createAsyncThunk(
  "analytics/getUserJourney",
  async ({ userId, sessionId }, { rejectWithValue, getState, dispatch }) => {
    try {
      const state = getState();
      const token = state.auth.token;
      const { baseURL, config } = generateConfig({ token });

      console.log(`[Analytics Thunk] Fetching user journey for session ${sessionId}`);

      const response = await axios.get(
        `${baseURL}/api/analytics/clicks/user-journey`,
        { ...config, params: { userId, sessionId } }
      );

      return response.data;
    } catch (error) {
      return handleApiError(error, rejectWithValue, dispatch);
    }
  }
);

// Get analytics summary (dashboard overview)
export const getAnalyticsSummary = createAsyncThunk(
  "analytics/getAnalyticsSummary",
  async (daysBack = 7, { rejectWithValue, getState, dispatch }) => {
    try {
      const state = getState();
      const token = state.auth.token;
      const { baseURL, config } = generateConfig({ token });

      console.log(`[Analytics Thunk] Fetching analytics summary for last ${daysBack} days`);

      const response = await axios.get(
        `${baseURL}/api/analytics/clicks/summary`,
        { ...config, params: { daysBack } }
      );

      return response.data;
    } catch (error) {
      return handleApiError(error, rejectWithValue, dispatch);
    }
  }
);
